/**
 * /teach 路由入口判定（T18）。
 *
 * 纯逻辑：解析 URL ?mode= 与本地存储偏好，决定本次导航是否需要
 * 规范化到 /teach。不访问组件、不发起请求、不产生副作用。
 *
 * 规则（对应设计 4.2）：
 * 1. 嵌入壳（embed=1 / Tauri）优先：轻壳内不进入教学。
 * 2. 显式 mode=teach 触发；显式其它值（含非法值）一律不落到存储偏好。
 * 3. 无显式 mode 时，仅 / 或 /medical 允许已保存的 teach 偏好触发。
 * 4. 已在 /teach 不重复规范化（防重定向循环）；管理页等其他路由不受影响。
 */

import {
  normalizeDisplayModeParam,
  normalizeStoredDisplayMode,
} from "@shared/utils/displayMode";

/** 存储偏好允许触发教学入口的路径（设计 4.2 第 3 条） */
const TEACH_PREFERENCE_PATHS: ReadonlySet<string> = new Set(["/", "/medical"]);

/** 教学路由路径（规范化目标，也是重复规范化的守卫条件） */
export const TEACH_ROUTE_PATH = "/teach";

export interface TeachEntryInput {
  /** 目标路由 path（规范化前） */
  path: string;
  /** URL ?mode= 原始值（可为 string / string[] / null / undefined） */
  modeParam?: unknown;
  /** 已持久化的显示模式偏好原始值 */
  storedMode?: unknown;
  /** 是否处于 HIS 嵌入壳（embed=1 / Tauri 运行时） */
  embedShellActive?: boolean;
}

/** 从 query 值中取首个字符串；非字符串结构返回 null */
function firstString(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return null;
}

/**
 * 判定本次导航是否应规范化到 /teach。
 *
 * @returns 目标路径（"/teach"）或 null（保持原导航）
 */
export function resolveTeachLocation(input: TeachEntryInput): string | null {
  if (input.path === TEACH_ROUTE_PATH) return null; // 已在目标页，防循环
  if (input.embedShellActive) return null; // 规则 1：轻壳不进教学

  const rawMode = firstString(input.modeParam);
  const hasExplicitMode = rawMode !== null && rawMode.trim() !== "";
  if (hasExplicitMode) {
    // 规则 2：仅合法 teach 触发；其余显式值（含非法）不落到存储偏好
    return normalizeDisplayModeParam(rawMode) === "teach"
      ? TEACH_ROUTE_PATH
      : null;
  }

  // 规则 3：无显式模式时，仅 / 或 /medical 允许存储偏好触发
  if (!TEACH_PREFERENCE_PATHS.has(input.path)) return null;
  return normalizeStoredDisplayMode(input.storedMode) === "teach"
    ? TEACH_ROUTE_PATH
    : null;
}
